/**
 * Datas no fuso de Brasília — lado CLIENT (M2, rodada V2-COMERCIAL-AJUSTES3).
 *
 * Espelho da convenção de `server/lib/datetime/brasilia.ts` (R-TIMEZONE-BR):
 * offset fixo UTC-3, porque o Brasil não tem horário de verão desde 2019.
 * Se o DST voltar (improvável), refatorar pra Intl.DateTimeFormat com
 * timeZone 'America/Sao_Paulo' — até lá, o deslocamento fixo é intencional
 * e idêntico ao que o server usa (TZ_BR_OFFSET_MS).
 *
 * Por que existe: `new Date().toISOString().slice(0, 7|10)` devolve o mês/dia
 * em UTC. No Brasil (UTC-3), a partir das 21h do último dia do mês isso vira
 * o MÊS SEGUINTE (e "hoje" vira amanhã) — as telas do Comercial abriam no
 * período errado. Use SEMPRE estes helpers pra "mês atual" / "hoje" na UI.
 *
 * Cuidado clássico (bug #34): os getters getUTC* aqui são aplicados no
 * wall-clock JÁ deslocado (instant + offset), nunca no instant puro.
 */

import { formatarData } from './format-date';

/** Offset Brasília em milissegundos (UTC-3 = -180 min = -10_800_000 ms). */
export const TZ_BR_OFFSET_MS = -3 * 60 * 60 * 1000;

/**
 * Agora como Date (instant) — equivalente a `new Date()`, com nome explícito
 * pra deixar o uso centralizado (mesma semântica do `nowBR()` do server:
 * o Date é um instant; só a FORMATAÇÃO usa o offset BR).
 */
export function agoraBR(): Date {
  return new Date();
}

/**
 * Data de hoje em Brasília no formato `YYYY-MM-DD`.
 * Aceita um Date opcional pra derivar a data BR de outro instant
 * (ex.: "daqui a 7 dias").
 */
export function hojeBR(d: Date = agoraBR()): string {
  const wallBR = new Date(d.getTime() + TZ_BR_OFFSET_MS);
  const yyyy = wallBR.getUTCFullYear();
  const mm = String(wallBR.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(wallBR.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * O dia de Brasília de um instante, como um `Date` de GRADE (meia-noite local com
 * o ano/mês/dia de Brasília). Para as grades de calendário que montam os dias com
 * `new Date(ano, mes, dia)`: "hoje" e o dia de cada evento passam a ser os de
 * Brasília, em qualquer fuso do computador (#484). Formate com `formatarDiaDaGrade`.
 */
export function diaDaGrade(instante: Date = agoraBR()): Date {
  const [y, m, d] = hojeBR(instante).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Mês atual em Brasília no formato `YYYY-MM` — o corte que as telas do
 * Comercial (Indicadores, Metas, Funil, Home) usam como período padrão.
 */
export function mesAtualBR(d: Date = agoraBR()): string {
  return hojeBR(d).slice(0, 7);
}

/**
 * Data e hora em Brasília, no formato forense do Histórico:
 * `10/09/2026, 21:26` (a vírgula, como o WhatsApp do V1). Aceita as strings
 * do SQLite (`YYYY-MM-DD HH:MM:SS`, UTC sem `Z`) e ISO com/sem `Z`.
 *
 * Adendo 11/09: "há 1 dia" não cruza com nada — uma data e um horário cruzam
 * com tudo. NUNCA o fuso do navegador: o offset fixo da casa.
 */
export function dataHoraBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  let s = iso.trim();
  // String do SQLite sem timezone é UTC — a convenção do banco inteiro. (Aqui até
  // o `YYYY-MM-DDTHH:MM` é instante: é o carimbo do Histórico, não um agendamento.)
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(s)) s = `${s.replace(' ', 'T')}Z`;
  // #484: a formatação mora na função única.
  return formatarData(s, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
